import { useState, type ReactNode } from 'react';

/**
 * Gráficas del panel de dirección.
 *
 * Dibujadas a mano en SVG, sin librería: son cuatro formas sencillas y así no
 * entra un paquete de 300 KB en un terminal que tiene que abrir rápido.
 *
 * Reglas que se siguen en todas:
 *  · Una sola escala por gráfica. Nunca dos ejes.
 *  · Los colores identifican la serie, nunca su posición en el ranking.
 *  · Rejilla y ejes discretos; el dato es lo que tiene que verse.
 *  · Con dos series o más, leyenda siempre: el color nunca es la única pista.
 *  · Al pasar por encima, el valor exacto. Etiquetas directas solo donde
 *    aportan, no un número encima de cada barra.
 */

/** Paleta comprobada contra el fondo oscuro de la aplicación (#131c2e). */
export const SERIE = {
  produciendo: '#3987e5',
  parado: '#d95926',
} as const;

const REJILLA = 'rgba(147, 167, 201, 0.16)';
const TINTA_TENUE = '#93a7c9';

export interface Segmento {
  clave: string;
  valor: number;
  color: string;
}

export interface Barra {
  etiqueta: string;
  /** Etiqueta corta para el eje; la larga se reserva al tooltip. */
  eje?: string;
  segmentos: Segmento[];
}

interface Tooltip {
  x: number;
  y: number;
  titulo: string;
  filas: { clave: string; valor: string; color: string }[];
}

function useTooltip() {
  const [tooltip, setTooltip] = useState<Tooltip | null>(null);
  const capa = tooltip ? (
    <div
      style={{
        position: 'absolute',
        left: Math.max(8, tooltip.x - 70),
        top: Math.max(0, tooltip.y - 8),
        transform: 'translateY(-100%)',
        background: 'var(--surface-3)',
        border: '1px solid var(--line)',
        borderRadius: 10,
        padding: '9px 12px',
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
        boxShadow: 'var(--shadow)',
        zIndex: 5,
      }}
    >
      <div style={{ fontWeight: 640, marginBottom: 4 }}>{tooltip.titulo}</div>
      {tooltip.filas.map((f) => (
        <div key={f.clave} style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 7 }}>
          <span style={{ width: 9, height: 9, borderRadius: 2, background: f.color }} />
          <span className="muted">{f.clave}</span>
          <span className="mono" style={{ marginLeft: 'auto', fontWeight: 600 }}>{f.valor}</span>
        </div>
      ))}
    </div>
  ) : null;
  return { tooltip, setTooltip, capa };
}

export function Leyenda({ series }: { series: { clave: string; color: string }[] }) {
  if (series.length < 2) return null; // con una sola serie, el título ya la nombra
  return (
    <div className="row row--wrap small" style={{ gap: 16 }}>
      {series.map((s) => (
        <span key={s.clave} style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          <span style={{ width: 11, height: 11, borderRadius: 3, background: s.color }} />
          <span className="muted">{s.clave}</span>
        </span>
      ))}
    </div>
  );
}

export function Grafica({ titulo, sub, children, acciones }: { titulo: string; sub?: string; children: ReactNode; acciones?: ReactNode }) {
  return (
    <section className="card" style={{ position: 'relative' }}>
      <div className="row" style={{ marginBottom: 14 }}>
        <div>
          <div className="h2">{titulo}</div>
          {sub ? <div className="muted small">{sub}</div> : null}
        </div>
        <div className="spacer" />
        {acciones}
      </div>
      {children}
    </section>
  );
}

export function SinDatos({ texto }: { texto: string }) {
  return (
    <div className="muted small" style={{ padding: '34px 0', textAlign: 'center' }}>
      {texto}
    </div>
  );
}

/** Camino de una barra con el extremo del dato redondeado (4 px). */
function caminoBarra(x: number, y: number, w: number, h: number, redondear: boolean, vertical: boolean): string {
  const r = Math.min(4, w / 2, h / 2);
  if (!redondear || r <= 0) return `M${x},${y}h${w}v${h}h${-w}Z`;
  if (vertical) {
    // Se redondea arriba, que es donde termina el dato; abajo se ancla al eje.
    return `M${x},${y + r}a${r},${r} 0 0 1 ${r},${-r}h${w - 2 * r}a${r},${r} 0 0 1 ${r},${r}v${h - r}h${-w}Z`;
  }
  return `M${x},${y}h${w - r}a${r},${r} 0 0 1 ${r},${r}v${h - 2 * r}a${r},${r} 0 0 1 ${-r},${r}h${-(w - r)}Z`;
}

/** Barras verticales apiladas. Para series de tiempo cortas: un día, una barra. */
export function BarrasDia({
  barras,
  series,
  formato,
  altura = 190,
}: {
  barras: Barra[];
  series: { clave: string; color: string }[];
  formato: (v: number) => string;
  altura?: number;
}) {
  const { setTooltip, capa } = useTooltip();
  const maximo = Math.max(1, ...barras.map((b) => b.segmentos.reduce((s, x) => s + x.valor, 0)));
  const total = barras.reduce((s, b) => s + b.segmentos.reduce((t, x) => t + x.valor, 0), 0);
  if (total === 0) return <SinDatos texto="Todavía no hay producción registrada en este periodo." />;

  const ancho = 100 / barras.length;
  const grosor = Math.min(ancho * 0.62, 7);

  return (
    <div style={{ position: 'relative' }}>
      <svg viewBox={`0 0 100 ${altura}`} preserveAspectRatio="none" style={{ width: '100%', height: altura }} role="img">
        {[0.25, 0.5, 0.75, 1].map((f) => (
          <line key={f} x1={0} x2={100} y1={altura - altura * f} y2={altura - altura * f} stroke={REJILLA} strokeWidth={0.5} vectorEffect="non-scaling-stroke" />
        ))}
        {barras.map((barra, i) => {
          const x = i * ancho + (ancho - grosor) / 2;
          let acumulado = 0;
          const suma = barra.segmentos.reduce((s, x2) => s + x2.valor, 0);
          return (
            <g
              key={barra.etiqueta}
              onMouseEnter={(e) =>
                setTooltip({
                  x: e.currentTarget.getBoundingClientRect().left - (e.currentTarget.closest('div')?.getBoundingClientRect().left ?? 0) + 20,
                  y: altura - (suma / maximo) * (altura - 10),
                  titulo: barra.etiqueta,
                  filas: barra.segmentos.map((s) => ({ clave: s.clave, valor: formato(s.valor), color: s.color })),
                })
              }
              onMouseLeave={() => setTooltip(null)}
            >
              {/* Zona de pulsación más grande que la barra, para que no haya que afinar. */}
              <rect x={i * ancho} y={0} width={ancho} height={altura} fill="transparent" />
              {barra.segmentos.map((seg, j) => {
                if (seg.valor <= 0) return null;
                const h = (seg.valor / maximo) * (altura - 10);
                // 2 px de hueco entre segmentos: el corte se ve aunque los
                // colores contrasten poco en una pantalla de taller.
                const y = altura - acumulado - h;
                acumulado += h + 2;
                return (
                  <path
                    key={seg.clave}
                    d={caminoBarra(x, y, grosor, Math.max(h - (j > 0 ? 2 : 0), 1), j === barra.segmentos.length - 1, true)}
                    fill={seg.color}
                  />
                );
              })}
            </g>
          );
        })}
      </svg>
      <div className="row" style={{ justifyContent: 'space-between', marginTop: 6 }}>
        <span className="small" style={{ color: TINTA_TENUE }}>{barras[0]?.eje ?? barras[0]?.etiqueta}</span>
        <span className="small" style={{ color: TINTA_TENUE }}>{barras[barras.length - 1]?.eje ?? barras[barras.length - 1]?.etiqueta}</span>
      </div>
      {capa}
    </div>
  );
}

/** Barras horizontales ordenadas de mayor a menor: dónde se va el tiempo. */
export function BarrasRanking({
  barras,
  series,
  formato,
  vacio,
}: {
  barras: Barra[];
  series: { clave: string; color: string }[];
  formato: (v: number) => string;
  vacio: string;
}) {
  const { setTooltip, capa } = useTooltip();
  const totales = barras.map((b) => b.segmentos.reduce((s, x) => s + x.valor, 0));
  const maximo = Math.max(1, ...totales);
  if (totales.every((t) => t === 0)) return <SinDatos texto={vacio} />;

  return (
    <div className="stack" style={{ gap: 12, position: 'relative' }}>
      {barras.map((barra, i) => {
        const suma = totales[i] as number;
        return (
          <div key={barra.etiqueta}>
            <div className="row" style={{ justifyContent: 'space-between', marginBottom: 5 }}>
              <span className="small">{barra.etiqueta}</span>
              {/* Etiqueta directa solo del total: el desglose está al pasar por encima. */}
              <span className="small mono muted">{formato(suma)}</span>
            </div>
            <div
              style={{ position: 'relative', height: 14, display: 'flex', gap: 2 }}
              onMouseEnter={(e) => {
                const caja = e.currentTarget.getBoundingClientRect();
                const padre = e.currentTarget.closest('.stack')?.getBoundingClientRect();
                setTooltip({
                  x: caja.left - (padre?.left ?? 0) + 60,
                  y: caja.top - (padre?.top ?? 0),
                  titulo: barra.etiqueta,
                  filas: barra.segmentos.map((s) => ({ clave: s.clave, valor: formato(s.valor), color: s.color })),
                });
              }}
              onMouseLeave={() => setTooltip(null)}
            >
              {barra.segmentos.map((seg, j) => {
                if (seg.valor <= 0) return null;
                const w = (seg.valor / maximo) * 100;
                const ultimo = j === barra.segmentos.filter((s) => s.valor > 0).length - 1;
                return (
                  <div
                    key={seg.clave}
                    style={{
                      width: `${w}%`,
                      background: seg.color,
                      borderRadius: ultimo ? '3px 4px 4px 3px' : 3,
                      minWidth: 3,
                    }}
                  />
                );
              })}
            </div>
          </div>
        );
      })}
      <Leyenda series={series} />
      {capa}
    </div>
  );
}

/**
 * Indicador con su medidor. Para un número que se mira de un vistazo: no es una
 * gráfica, y forzarlo a serlo solo añade ruido.
 */
export function Indicador({
  etiqueta,
  valor,
  sub,
  proporcion,
  tono,
}: {
  etiqueta: string;
  valor: string;
  sub?: string;
  proporcion?: number;
  tono?: 'ok' | 'warn' | 'danger';
}) {
  const color = tono === 'ok' ? 'var(--ok)' : tono === 'warn' ? 'var(--warn)' : tono === 'danger' ? 'var(--danger)' : undefined;
  return (
    <div className="kpi__card">
      <div className="kpi__label">{etiqueta}</div>
      <div className="kpi__value" style={color ? { color } : undefined}>{valor}</div>
      {proporcion !== undefined ? (
        <div className="bar">
          <div className="bar__fill" style={{ width: `${Math.round(Math.max(0, Math.min(1, proporcion)) * 100)}%`, background: color ?? SERIE.produciendo }} />
        </div>
      ) : null}
      {sub ? <div className="small muted" style={{ marginTop: 8 }}>{sub}</div> : null}
    </div>
  );
}

/**
 * Consumido contra previsto, en una sola línea.
 *
 * Es un gráfico de bala: la barra es lo que se lleva gastado y la marca
 * vertical es lo que la ruta de SAP preveía. Dos números del mismo tipo
 * —segundos— en un solo eje; si fueran de tipos distintos harían falta dos
 * gráficos, no dos escalas.
 *
 * Lo que se pasa de lo previsto se pinta aparte y con su etiqueta: en una
 * pantalla colgada en el taller nadie va a pasar el ratón por encima, así que
 * el dato tiene que estar escrito.
 */
export function Bala({
  consumido,
  parado = 0,
  previsto,
  formato,
  altura = 10,
}: {
  consumido: number;
  parado?: number;
  previsto: number;
  formato: (v: number) => string;
  altura?: number;
}) {
  const trabajado = Math.max(0, consumido - parado);
  const total = trabajado + parado;
  const escala = Math.max(total, previsto, 1) * 1.08;
  const pct = (v: number) => `${(v / escala) * 100}%`;
  // Pasarse un 20 % de lo previsto es un problema del taller. Pasarse diez
  // veces no: es que nadie ha rellenado los tiempos de la ruta en SAP. Decir
  // «+123 h de más» en ese caso es dar por bueno un previsto que no existe.
  const sinRuta = previsto > 0 && total > previsto * 10;
  const excedido = previsto > 0 && total > previsto && !sinRuta;

  return (
    <div className="stack" style={{ gap: 4 }}>
      <div
        style={{
          position: 'relative',
          height: altura,
          background: 'rgba(147, 167, 201, 0.14)',
          borderRadius: altura / 2,
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'absolute', inset: 0, display: 'flex', gap: 2 }}>
          <div style={{ width: pct(trabajado), background: SERIE.produciendo, borderRadius: altura / 2 }} />
          {parado > 0 ? (
            <div style={{ width: pct(parado), background: SERIE.parado, borderRadius: altura / 2 }} />
          ) : null}
        </div>
        {previsto > 0 && !sinRuta ? (
          // La marca de lo previsto va ENCIMA de la barra, no al final: así se
          // ve si falta o si se ha pasado sin tener que comparar longitudes.
          <div
            style={{
              position: 'absolute',
              left: pct(previsto),
              top: -2,
              bottom: -2,
              width: 2,
              background: '#e8eefb',
              boxShadow: '0 0 0 1px rgba(19, 28, 46, 0.9)',
            }}
          />
        ) : null}
      </div>
      <div className="row" style={{ justifyContent: 'space-between', gap: 8 }}>
        <span className="small mono">{formato(total)}</span>
        <span className="small mono muted">
          {previsto <= 0
            ? 'sin tiempo en la ruta'
            : sinRuta
              ? `ruta sin tiempos reales (dice ${formato(previsto)})`
              : `previsto ${formato(previsto)}`}
          {excedido ? (
            <span style={{ color: 'var(--danger)' }}> · +{formato(total - previsto)} de más</span>
          ) : null}
        </span>
      </div>
    </div>
  );
}
